import { type CSSProperties, useCallback, useMemo } from "react";

/**
 * Custom hook to generate cell properties for table components.
 *
 * @param hidden - A boolean indicating whether the cell should be hidden.
 * @returns An object containing:
 * - `hiddenStyles`: Styles applied when the cell is hidden.
 * - `propGetter`: A function to generate props for styled components, merging custom styles with hidden styles.
 */
const useCellProps = (
  hidden: boolean,
): { hiddenStyles: any; propGetter: (styles?: CSSProperties) => any } => {
  const hiddenStyles = useMemo(
    () =>
      hidden
        ? {
            visibility: "hidden",
            borderColor: "transparent",
            pointerEvents: "none",
          }
        : {},
    [hidden],
  );

  // use propGetter function oly for StyledTd, StyledTh, StyledTr etc. (styled components that have withoutImportant prop!!!)
  /**
   * Generates props for styled components, merging custom styles with hidden styles.
   *
   * @param styles - Optional. Custom styles to be merged with hidden styles.
   * @returns An object containing the merged styles and a `withoutImportant` flag.
   */
  const propGetter = useCallback(
    (styles?: CSSProperties): any => ({
      withoutImportant: hidden,
      style: {
        ...styles,
        ...hiddenStyles,
      },
    }),
    [hidden, hiddenStyles],
  );

  return {
    hiddenStyles,
    propGetter,
  };
};
export default useCellProps;
